/**
 * 핫클립 취향 학습 (2026-10-05 사장님: 인스타 돋보기처럼 음악 쇼츠를 큐레이션하고, 고른 영상에 따라 다음 영상도 추천).
 * 기기마다 이 휴대폰에서 본 기록으로 취향을 배운다 (서버로 개인 정보를 보내지 않음).
 * - 꼬리표(연주 스타일·장르)는 AI 가 영상마다 붙인 것 (refresh_hotclips.py)
 * - 오래 본 영상의 꼬리표·채널·곡은 점수 +, 2~3초 만에 넘긴 영상은 점수 −
 * - 목록(격자) 순서 = 서버 순위(회원 전체 반응·조회수) + 내 취향
 * - 영상 하나를 고르면 그다음은 그 영상과 비슷한 것(같은 곡 > 같은 채널 > 같은 꼬리표) + 내 취향 순
 */
import { IHotClip } from "../pages/HotClips";

const KEY = "hcTaste";

interface Taste {
  w: Record<string, number>; // 꼬리표·채널(ch:)·곡(m:) 점수
  seen: Record<string, number>; // 영상별 마지막으로 본 시각(ms)
}

const load = (): Taste => {
  try {
    const t = JSON.parse(localStorage.getItem(KEY) || "");
    if (t && t.w && t.seen) return t;
  } catch (e) {}
  return { w: {}, seen: {} };
};

const save = (t: Taste) => {
  try {
    // 오래된 기록은 줄여서 저장 (최근 300개 영상만)
    const ids = Object.keys(t.seen).sort((a, b) => t.seen[b] - t.seen[a]).slice(0, 300);
    const seen: Record<string, number> = {};
    ids.forEach((k) => (seen[k] = t.seen[k]));
    localStorage.setItem(KEY, JSON.stringify({ w: t.w, seen }));
  } catch (e) {}
};

const keysOf = (c: IHotClip) => [...(c.tags || []), `ch:${c.channel}`, ...(c.music ? [`m:${c.music.id}`] : [])];

/** 영상을 본 시간(초)으로 취향 배우기 */
export const learn = (c: IHotClip, sec: number) => {
  const t = load();
  const d = sec >= 12 ? 1 : sec >= 5 ? 0.4 : sec < 3 ? -0.6 : 0;
  // 예전 취향은 조금씩 옅어지게 (요즘 취향이 더 중요)
  Object.keys(t.w).forEach((k) => (t.w[k] *= 0.98));
  keysOf(c).forEach((k) => {
    const scale = k.startsWith("ch:") ? 0.6 : k.startsWith("m:") ? 0.8 : 1;
    t.w[k] = Math.max(-5, Math.min(10, (t.w[k] || 0) + d * scale));
  });
  t.seen[c.id] = Date.now();
  save(t);
};

const affinity = (t: Taste, c: IHotClip) => {
  const ks = keysOf(c);
  return ks.reduce((s, k) => s + (t.w[k] || 0), 0) / Math.sqrt(ks.length || 1);
};

// 최근(하루 안)에 본 영상은 조금 뒤로
const seenPenalty = (t: Taste, c: IHotClip) => {
  const at = t.seen[c.id];
  if (!at) return 0;
  const h = (Date.now() - at) / 3600000;
  return h < 24 ? 0.6 : h < 72 ? 0.25 : 0;
};

/** 격자 순서: 서버 순위 + 내 취향 */
export const gridOrder = (clips: IHotClip[]): IHotClip[] => {
  const t = load();
  const n = clips.length || 1;
  return clips
    .map((c, i) => ({ c, s: (1 - i / n) * 2 + affinity(t, c) * 0.35 - seenPenalty(t, c) }))
    .sort((a, b) => b.s - a.s)
    .map((x) => x.c);
};

const similarity = (a: IHotClip, b: IHotClip) => {
  let s = 0;
  if (a.music && b.music && a.music.id === b.music.id) s += 1.5;
  if (a.channel === b.channel) s += 0.6;
  const at = new Set(a.tags || []);
  (b.tags || []).forEach((x) => at.has(x) && (s += 0.5));
  return s;
};

/** 고른 영상을 맨 앞에, 그다음은 비슷한 것 + 내 취향 순 */
export const feedOrder = (clips: IHotClip[], startId: string | null): IHotClip[] => {
  const start = clips.find((c) => c.id === startId);
  if (!start) return gridOrder(clips);
  const t = load();
  const n = clips.length || 1;
  const rest = clips
    .filter((c) => c.id !== start.id)
    .map((c) => ({
      c,
      s: similarity(start, c) + affinity(t, c) * 0.3 + (1 - clips.indexOf(c) / n) * 0.6 - seenPenalty(t, c),
    }))
    .sort((a, b) => b.s - a.s)
    .map((x) => x.c);
  return [start, ...rest];
};
